import { Box } from '@mui/material'
import { useEffect, useState, useContext } from 'react'
import { type Option, type Branch, type Log } from 'src/types'

// context
import { EventDataContext } from 'src/store/events/Store'
import { SaveDataContext } from 'src/store/save/Store'
import { NavigationDispatchContext } from 'src/store/navigation/Store'
import { LogDispatchContext } from 'src/store/logs/Store'

// actions
import { setSwitch } from 'src/store/navigation/actions'
import { addLog, deleteLogs } from 'src/store/logs/actions'

// hooks
import useSharedStore from 'src/hooks/useSharedStore'

// components
import Cinematic from 'src/components/common/Cinematic'
import Options from 'src/components/common/Options'
import Cue from 'src/components/common/Cue'
import Subtitle from 'src/components/common/Subtitle'
import useKeyPress from 'src/hooks/useKeyPress'

// utils

const containerStyle = {
  zIndex: 4,
  position: 'absolute',
  top: '64px',
  width: '100%',
  maxHeight: '50vh',
  display: 'flex',
  flexDirection: 'column'
}

const cinematicContainerStyle = {
  ...containerStyle,
  top: 0,
  maxHeight: '100vh',
  height: '100vh',
  backgroundColor: 'black'
}

const cinematicWrapperStyle = {
  position: 'relative',
  height: '100%',
  width: '100%',
  transform: 'scale(calc(100vw - 256px))'
}

const Event = () => {
  const { save } = useContext(SaveDataContext)
  const { dispatchNavigation } = useContext(NavigationDispatchContext)
  const { dispatchLogs } = useContext(LogDispatchContext)
  const { events } = useContext(EventDataContext)
  const event = events[save.variables.event_id] || events.event_not_found

  // sharedStoreActions
  const { processEffects, checkConditions} = useSharedStore()

  // filter branches based on conditions
  const filteredBranches: Branch[] = event?.branches?.filter(branch => checkConditions(branch.conditions)) || []
  const [currentBranch, setCurrentBranch] = useState(filteredBranches[0])
  const [logIndex, setLogIndex] = useState(0)

  const currentBranchFilteredOptions = currentBranch?.options?.filter(option => checkConditions(option.conditions)) || []

  useEffect(() => {
    if (!currentBranch) {
      setSwitch(dispatchNavigation, 'event_open', false)
    } else if (!currentBranch.logs?.length && !currentBranch.options?.length) {
      if (currentBranch.effects) {
        processEffects(currentBranch.effects)
      }
      setSwitch(dispatchNavigation, 'event_open', false)
    }
  }, [currentBranch])

  // if branch has no logs and no options process the effects without rendering
  useEffect(() => {
    const filteredBranches: Branch[] = event?.branches?.filter(branch => checkConditions(branch.conditions)) || []
    if (filteredBranches.length > 0) {
      setCurrentBranch(filteredBranches[0])
      setLogIndex(0)
    } else {
      setSwitch(dispatchNavigation, 'event_open', false)
    }
  }, [save])

  const handleDefaultOption = () => {
    const log = currentBranch.logs[logIndex]
    addLog(dispatchLogs, log)
    // if it's not the last log
    if (logIndex + 1 < currentBranch.logs.length) {
      setLogIndex(logIndex + 1)
    // if it's the last log and has effects
    } else if (currentBranch.effects && Object.keys(currentBranch.effects).length > 0) {
      processEffects(currentBranch.effects)
    // if it's the last log and has no effects
    } else {
      setSwitch(dispatchNavigation, 'event_open', false)
    }
  }

  const handleOption = (option: Option) => {
    const log: Log = {
      cue: option.text || currentBranch.logs[logIndex].cue, // prioritises the option if available
      text: currentBranch.logs?.length ? currentBranch.logs[logIndex].text : '',
      omen: currentBranch.logs?.length ? currentBranch.logs[logIndex].omen : undefined,
      author: currentBranch.logs[logIndex].author
    }

    addLog(dispatchLogs, log)
    if (currentBranch.effects) {
      const newEffects = {
        ...option.effects,
        ...currentBranch.effects,
        switches: {
          ...option.effects.switches,
          ...currentBranch.effects.switches
        }
      }
      processEffects(newEffects)
    } else {
      processEffects(option.effects)
    }

    if (option.end) {
      setSwitch(dispatchNavigation, 'event_open', false)
    }
  }

  // clear logs on unmount
  useEffect(() => () => {
    deleteLogs(dispatchLogs)
  }, [])

  const enterPressed = useKeyPress('Enter')

  useEffect(() => {
    // if it's cinematic, not in the last log, and no log options
    if (enterPressed && isDefaultOptionValid) {
      handleDefaultOption()
    }
  }, [enterPressed])

  // development build logs
  useEffect(() => {
    if (process.env.REACT_APP_BENCHMARK === 'true') { console.log('Event mounted') }
    return process.env.REACT_APP_BENCHMARK === 'true' ? () => console.log('Event unmounted') : () => {}
  }, [])

  useEffect(() => {
    if (process.env.REACT_APP_BENCHMARK === 'true') { console.log('Event rendered') }
  })
  
  const isCinematic = currentBranch?.logs[logIndex].spriteID
  const isLastLog = logIndex + 1 === currentBranch?.logs.length
  const hasOptions = currentBranchFilteredOptions.length > 0
  const isDefaultOptionValid = isCinematic && (!isLastLog || !hasOptions)
  
  // for events that have effects but are invisible
  const isInvisibleEvent = !currentBranch || (currentBranch?.logs.length === 0 && (!currentBranch?.options?.length))
  
  // for default cues
  const logCue = currentBranch?.logs[logIndex].cue

  return !currentBranch || isInvisibleEvent ? null : (
    <Box sx={ currentBranch.logs[logIndex] && isCinematic ? cinematicContainerStyle : containerStyle} className="event">
      { currentBranch.logs[logIndex]
        ? isCinematic ? (
          <Box sx={cinematicWrapperStyle} className='cinematic_wrapper' onClick={isDefaultOptionValid ? handleDefaultOption : () => {}}>
            {/* @ts-expect-error */}
            <Cinematic spriteID={currentBranch.logs[logIndex].spriteID}/>
            <Box sx={{position: 'absolute', width: '100%', top: '0', padding: '16px'}}>
              { currentBranch.logs[logIndex]?.text ? (
                <Box sx={{ marginBottom: '16px' }}>
                  <Subtitle>
                    {currentBranch.logs[logIndex].text}
                  </Subtitle>
                </Box>
              ) : null }
              {
                isLastLog && currentBranchFilteredOptions.length > 0 ? (
                  <Options
                    options={currentBranchFilteredOptions}
                    handleOption={handleOption}
                    handleDefaultOption={handleDefaultOption}
                    logCue={(logCue || 'next')}
                  />
                ) : logCue ? (
                  <Options
                    options={[]}
                    handleOption={handleOption}
                    handleDefaultOption={handleDefaultOption}
                    logCue={(logCue || 'next')}
                  />
                ) : null
              }
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ margin: '16px 16px 0 16px' }} className='cue_wrapper'>
              <Cue log={currentBranch.logs[logIndex]}/>
            </Box>
            <Box sx={{margin: '16px 16px 48px 16px'}} className='options_wrapper'>
              {
                isLastLog && currentBranchFilteredOptions.length > 0 ? (
                  <Options
                    options={currentBranchFilteredOptions}
                    handleOption={handleOption}
                    handleDefaultOption={handleDefaultOption}
                    logCue={(logCue || 'next')}
                  />
                ) : (
                  <Options
                    options={[]}
                    handleOption={handleOption}
                    handleDefaultOption={handleDefaultOption}
                    logCue={(logCue || 'next')}
                  />
                )
              }
              
            </Box>
          </>
        ) : null }
      
    </Box>
  )
}

export default Event
